import type { HistoryMutator, HistoryStore } from './historyState.js';
import { COMMAND_MERGE, type EditorCommand } from './commandBuilders.js';

export interface CommandExecution<TResult, TAction> {
  result: TResult;
  undoActions: TAction[];
  changed: boolean;
  replayActions?: TAction[];
}

export interface CommandBatch {
  begin(label: string): void;
  commit(): void;
  discard(): void;
  run<T>(label: string, fn: () => T): T;
}

export interface CommandHistory {
  configure<TAction>(replay: (action: TAction) => unknown): void;
  runCommandWithHistory<TAction, TCommand extends TAction, TResult>(
    command: TCommand,
    label: string,
    execute: (command: TCommand) => CommandExecution<TResult, TAction>,
  ): TResult;
  commandBatch: CommandBatch;
}

function configureActionMerger(historyStore: Pick<HistoryStore, 'setActionMerger'>): void {
  historyStore.setActionMerger((existing, incoming) => {
    if (incoming.length !== 1 || existing.length === 0) return null;
    const next = incoming[0] as EditorCommand;
    const last = existing[existing.length - 1] as EditorCommand;
    if (last.type !== next.type) return null;
    const mergeFn = COMMAND_MERGE[last.type] as ((p: EditorCommand, n: EditorCommand) => EditorCommand | null) | undefined;
    if (!mergeFn) return null;
    const merged = mergeFn(last, next);
    if (!merged) return null;
    return [...existing.slice(0, -1), merged];
  });
}

function createCommandBatch(mutate: HistoryMutator): CommandBatch {
  return {
    begin(label: string): void {
      mutate.begin(label);
    },
    commit(): void {
      mutate.commit();
    },
    discard(): void {
      mutate.discard();
    },
    run<T>(label: string, fn: () => T): T {
      return mutate(label, fn);
    },
  };
}

export function createCommandHistory(historyStore: HistoryStore): CommandHistory {
  configureActionMerger(historyStore);

  return {
    configure<TAction>(replay: (action: TAction) => unknown): void {
      historyStore.setHistoryReplayer((payload) => replay(payload as TAction));
    },
    runCommandWithHistory<TAction, TCommand extends TAction, TResult>(
      command: TCommand,
      label: string,
      execute: (command: TCommand) => CommandExecution<TResult, TAction>,
    ): TResult {
      const execution = execute(command);
      if (execution.changed) {
        historyStore.pushHistoryEntry(label, execution.replayActions ?? [command], execution.undoActions);
      }
      return execution.result;
    },
    commandBatch: createCommandBatch(historyStore.mutate),
  };
}
